import { useEffect } from 'react';
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Col, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import FormInput from '@/components/forms/FormInput';
import { GlobalSettingsDto, isSettingType, SettingType } from '@repo/common-types';

interface EditSettingModalProps {
  setting: GlobalSettingsDto;
  onClose: () => void;
  onSave: (setting: GlobalSettingsDto) => void;
  formStatus?: {
    isLoading: boolean;
    error: string | null;
  };
}

const settingTypes = [SettingType.STRING, SettingType.NUMBER, SettingType.BOOLEAN, SettingType.DATE];

const getValidationSchema = () => {
  return Yup.object().shape({
    key: Yup.string()
      .required('Key is required')
      .matches(
        /^[a-zA-Z][a-zA-Z0-9_]*$/,
        'Key must start with a letter and contain only letters, numbers, and underscores',
      ),
    type: Yup.string().oneOf(settingTypes, 'Invalid type').required('Type is required'),
    value: Yup.string()
      .required('Value is required')
      .test('value-matches-type', 'Value must match the selected type', function (value) {
        const { type } = this.parent;
        if (!value || !type) return false;

        switch (type) {
          case SettingType.NUMBER: {
            const numberRegex = /^-?\d+(\.\d+)?$/;
            if (!numberRegex.test(value)) {
              return this.createError({ message: 'Value must be a valid number' });
            }
            break;
          }

          case SettingType.BOOLEAN: {
            if (!['true', 'false'].includes(value.toLowerCase())) {
              return this.createError({ message: 'Value must be true or false' });
            }
            break;
          }

          case SettingType.DATE: {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return this.createError({ message: 'Value must be a valid date' });
            }
            break;
          }

          case SettingType.STRING:
          default:
            if (typeof value !== 'string') {
              return this.createError({ message: 'Value must be a string' });
            }
            break;
        }
        return true;
      }),
    description: Yup.string().optional(),
  });
};

const EditSettingModal = ({ setting, onClose, onSave, formStatus }: EditSettingModalProps) => {
  console.log('setting', setting);
  const form = useForm({
    resolver: yupResolver(getValidationSchema()),
    mode: 'onChange',
    defaultValues: {
      key: setting.key,
      value: setting.value,
      type: isSettingType(setting.type) ? setting.type : SettingType.STRING,
    },
  });

  const {
    handleSubmit,
    watch,
    control,
    trigger,
    formState: { isDirty, isValid },
  } = form;

  const watchedType = watch('type');

  // Re-validate value when type changes
  useEffect(() => {
    trigger('value');
  }, [watchedType, trigger]);

  const onSubmit = (data: any) => {
    const updatedSetting: GlobalSettingsDto = {
      ...setting,
      ...data,
      updatedAt: new Date().toISOString(),
    };
    onSave(updatedSetting);
  };

  const getInputType = (type: string) => {
    switch (type) {
      case SettingType.NUMBER:
        return 'number';
      case SettingType.DATE:
        return 'datetime-local';
      case SettingType.BOOLEAN:
        return 'select';
      default:
        return 'text';
    }
  };

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="edit-setting-modal" className="modal-lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Edit Tournament Setting</ModalHeader>
          <ModalBody>
            <div className="w-100 d-flex flex-column justify-content-start gap-3">
              <Row>
                <Col>
                  <FormInput
                    control={control}
                    label="Setting Key"
                    name="key"
                    type="text"
                    placeholder="e.g., pauseTitle"
                  />
                </Col>
                <Col>
                  <FormInput
                    control={control}
                    disabled
                    name="type"
                    label="Type"
                    type="select"
                    options={settingTypes.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <FormInput
                    control={control}
                    name="value"
                    label="Value"
                    type={getInputType(watchedType)}
                    placeholder="Setting value"
                    options={
                      watchedType === SettingType.BOOLEAN
                        ? [
                            { value: 'true', label: 'True' },
                            { value: 'false', label: 'False' },
                          ]
                        : undefined
                    }
                  />
                </Col>
              </Row>

              {formStatus?.error && (
                <div className="text-danger mt-2" role="alert">
                  {formStatus?.error}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose} disabled={formStatus?.isLoading}>
              Cancel
            </Button>
            <Button disabled={!isDirty || !isValid || formStatus?.isLoading} type="submit" color="primary">
              {formStatus?.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default EditSettingModal;
